import DebugModuleVO from "../../debug/core/connector/DebugModuleVO";
import SkinKey from "../ui/SkinKey";
import MediatorMapping from "./MediatorMapping";

export default class AbstractInstaller {

    /**
     * @param {infuse.Injector} injector 
     * @param {soma.Commands} commands 
     * @param {MediatorMapping} mediatorMap 
     */
    constructor(injector, commands, mediatorMap) {
        this._injector = injector;
        this._commands = commands;
        this._mediatorMap = mediatorMap;
    }

    /**
     * Applies an installer
     * @param {Function} installerClass - Class that extends AbstractInstaller
     */
    _install(installerClass) {
        this._injector.createInstance(installerClass);
    }

    /**
     * Shortcut for this._injector.getValue
     * @param {String} label - label by which to find the object in the injector mapping
     * @returns 
     */
    _getValue(label) {
        return this._injector.getValue(label);
    }

    /**
     * Add task to startup sequence that will execute after preload completes.
     * At that point, the config.json and assets.json are loaded.
     * @param {Function} taskClass - Task (sub)class
     * @param {String} id - Reference id for internal use and for specifying dependencies.
     * @param {Number} duration - Estimated duration of task. All tasks should specify a duration in a comparable quantity
     * @param {Array<String>} dependencies - Optional array of ids of tasks on the completeness of which this task depends.
     */
    _addStartupTask(taskClass, id, duration, dependencies) {
        const startup = this._injector.getValue("startup");
        startup.addTask(taskClass, id, duration, dependencies);
    }

    /**
     * Add assets to the assetConfigurationManifest that is loaded BEFORE any other assets are loaded.
     * @param  {...Object} assets - Assets are files specified as objects with keys 'id' and 'src'.
     */
    _addAssetsToConfigurationManifest(...assets) {
        //Create assetConfigurationManifest if it does not exist
        this.__addToList("assetConfigurationManifest", assets);
    }

    /**
     * Add asset groups to one of the mapped asset lists.
     * Typically, the asset group lists are mapped in the main installer of the project (AppInstaller)
     * The manifest should be either defined in assets.json or be an exported adobe animate skin file.
     * @param {String} listId - Id of previously mapped asset group list. See AppInstaller.
     * @param  {...String} groups 
     */
    _addAssetGroupsToList(listId, ...groups) {
        this.__addToList(listId, groups);
    }

    /**
     * Add events to the list of events that will be dispatched when app.destroy is called.
     * @param  {...String} eventTypes 
     */
    _addDestroyEvents(...eventTypes) {
        this.__addToList("destroyEventList", eventTypes);
    }

    /**
     * Add module to the list of debug modules to install.
     * The debug tools will be installed by the InitDebugToolsTask
     * @param {String} displayName - display name of module, for menu
     * @param {Function} classFn - constructor of module class
     */
    _addDebugModule(displayName, classFn) {
        this.__addToList("debugModuleList", [new DebugModuleVO(displayName, classFn)]);
    }

    /**
     * Add items to a mapped asset lists. If list does not exist, create it.
     * @param {String} listId - Id of previously mapped list. See AppInstaller. 
     * @param {...any} items - Items to be added
     */
    __addToList(listId, items) {
        let list;
        if (this._injector.hasMapping(listId)) {
            list = this._injector.getValue(listId);
        } else {
            list = [];
            this._injector.mapValue(listId, list);
            console.info(`${this.constructor.name} created mapped list with key '${listId}'`);
        }
        list.push.apply(list, items);
    }

    
    /**
     * Register a skin by creating a unique key and mapping it in the mediatorMap.
     * Either mediatorClass or skinClass can be null, but there is no use case for both being null.
     * - Typically, mediatorClass will be null when the skin doesn't need a mediator, 
     *   and the returned key is only used for instantiating the skin through the SkinFactory.
     * - Typically, skinClass will be null if your code 'manually' calls mediatorManager.createMediator 
     *   for an existing DisplayObject, 
     * and we only want to add the returned key to the mediatorMap for that.
     * @param {Function} mediatorClass
     * @param {Function} skinClass - class/function for the skin DisplayObject. 
     * @returns {SkinKey} - Skin class wrapped in an object
     */
     _mapMediator(mediatorClass, skinClass = null) {
        //wrap skinClass so it becomes a unique object, so skinClass can be used as key multiple times 
        //(e.g. when using createjs.Container as skinClass, or null for 'manually' created mediators)    
        const key = new SkinKey(skinClass);
        if (mediatorClass != null) {
            this._mediatorMap.map(key, mediatorClass);
        }
        return key;
    }

}

export {};